import React, { useState, useRef, Suspense, useCallback, useMemo, useContext } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import { ChatMessageItem } from "@aidkitorg/types/lib/convoai";
import { getDistroBrowserIncompatibility } from "../../Util";
import { useLocation } from "react-router-dom";
import { usePost } from "../../API";
import { Button, useToast, Icon, nonHookMarkdown } from "@aidkitorg/component-library";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import InterfaceContext from "../../Context";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

const blankTemplate: survey.EditableMessageTemplate = {
  kind: "Message Template",
  name: "",
  version: 0,
  description: "",
  instructions: []
};

type StoredTemplate = survey.MessageTemplate & {
  underlyingInstructions?: survey.Instruction[];
  underlyingOutput?: survey.OutputFormat;
};

type TemplateOption = {
  label: string;
  value: string;
  template: StoredTemplate;
};

function convertStoredTemplateToEditable(storedTemplate: StoredTemplate): survey.EditableMessageTemplate {
  const { underlyingInstructions, underlyingOutput, ...templateFields } = storedTemplate;
  const template: survey.EditableMessageTemplate = {
    ...templateFields,
    kind: 'Message Template',
  };

  if (underlyingInstructions) {
    template.instructions = underlyingInstructions;
  }

  if (underlyingOutput) {
    template.output = underlyingOutput;
  }

  return template;
}

function getInstructionSummary(instruction: survey.Instruction): { title: string; subtitle?: string } {
  switch (instruction.kind) {
    case 'Text Instruction':
      const content = instruction.content['en'] || Object.values(instruction.content)[0] || '';
      const preview = content.length > 60 ? content.slice(0, 60) + '...' : content;
      return {
        title: instruction.role || 'system',
        subtitle: preview
      };
    case 'Conversation Part':
      return { title: 'Conversation', subtitle: instruction.part };
    case 'Example Images':
      return { title: 'Example Images', subtitle: `${instruction.imageUrls.length} image(s)` };
    case 'Message Template Reference':
      return {
        title: 'Template Reference',
        subtitle: `${instruction.templateRef.name}${instruction.templateRef.version !== undefined ? ` v${instruction.templateRef.version}` : ''}`
      };
    default:
      return { title: 'Unknown' };
  }
}

function CollapsibleInstruction({ instruction, index, defaultExpanded = false }: {
  instruction: survey.Instruction;
  index: number;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const summary = getInstructionSummary(instruction);

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <Icon
          name="ChevronRightIcon"
          className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
        <span className="text-xs text-gray-400 font-mono">{index + 1}</span>
        <span className="text-xs font-medium text-gray-600 uppercase">{summary.title}</span>
        {summary.subtitle && (
          <span className="text-xs text-gray-500 truncate flex-1">{summary.subtitle}</span>
        )}
      </button>
      {isExpanded && (
        <div className="p-2">
          <InstructionContent instruction={instruction} />
        </div>
      )}
    </div>
  );
}

function InstructionContent({ instruction }: { instruction: survey.Instruction }) {
  const { lang } = useContext(InterfaceContext);
  switch (instruction.kind) {
    case 'Text Instruction':
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {instruction.role || 'system'}
            </span>
            {instruction.tag && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                {instruction.tag}
              </span>
            )}
          </div>
          <div className="prose prose-sm max-w-none text-gray-700">
            {nonHookMarkdown(instruction.content[lang ?? 'en'])}
          </div>
        </div>
      );
    case 'Conversation Part':
      return (
        <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
          <div className="flex items-center gap-2">
            <Icon name="ChatBubbleLeftRightIcon" className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">
              Conversation: <code className="bg-purple-100 px-1 rounded">{instruction.part}</code>
            </span>
          </div>
        </div>
      );
    case 'Example Images':
      return (
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="PhotoIcon" className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Example Images</span>
          </div>
          {instruction.description && (
            <p className="text-xs text-green-600 mb-2">{instruction.description}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {instruction.imageUrls.map((url, i) => (
              <div key={i} className="relative">
                <img
                  src={url}
                  alt={`Example ${i + 1}`}
                  className="w-16 h-16 object-cover rounded border border-green-300"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      );
    case 'Message Template Reference':
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="flex items-center gap-2">
            <Icon name="DocumentDuplicateIcon" className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">
              Template Reference: <code className="bg-amber-100 px-1 rounded">{instruction.templateRef.name}</code>
              {instruction.templateRef.version !== undefined && (
                <span className="text-xs text-amber-500 ml-1">v{instruction.templateRef.version}</span>
              )}
            </span>
          </div>
        </div>
      );
    default:
      return null;
  }
}

function OutputFormatPreview({ output }: { output: survey.OutputFormat }) {
  if (output.type === 'template') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
        <div className="flex items-center gap-2">
          <Icon name="DocumentDuplicateIcon" className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-700">
            Output from template: <code className="bg-amber-100 px-1 rounded">{output.templateRef.name}</code>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <pre className="text-xs text-slate-700 overflow-auto">
        {JSON.stringify(formatOutputSchema(output), null, 2)}
      </pre>
    </div>
  );
}

function formatOutputSchema(output: survey.ObjectDef): object {
  const schema: Record<string, unknown> = {};
  for (const field of output.fields || []) {
    if (typeof field.type === 'string') {
      schema[field.name] = field.type;
    } else if (field.type.type === 'string') {
      schema[field.name] = 'string' + (field.type.format ? ` (format: ${field.type.format})` : '');
    } else if (field.type.type === 'enum') {
      schema[field.name] = `enum(${field.type.values.join(' | ')})`;
    } else if (field.type.type === 'array') {
      schema[field.name] = `array<${JSON.stringify(field.type.items)}>`;
    } else if (field.type.type === 'object') {
      schema[field.name] = formatOutputSchema(field.type);
    }
  }
  return schema;
}

function chatItemsToMarkdown(items: ChatMessageItem[]): string {
  const lines: string[] = [];

  for (const item of items) {
    if (item.type === 'message') {
      for (const part of item.content) {
        if (part.type === 'text') {
          lines.push(part.content);
        } else if (part.type === 'image') {
          lines.push(`![image](${part.url})`);
        }
      }
      lines.push('');
    }
  }

  return lines.join('\n');
}

function MessageTemplatePreview({ template }: { template: survey.EditableMessageTemplate }) {
  const { toast } = useToast();
  const previewRequest = usePost('/program/admin/prompts/preview');
  const [isCopying, setIsCopying] = useState(false);

  const handleCopy = useCallback(async () => {
    if (template.instructions.length === 0) {
      toast({
        description: 'No instructions to copy',
        autoClose: true,
        duration: 1_500
      });
      return;
    }

    setIsCopying(true);
    try {
      const { items } = await previewRequest({ item: template });
      const markdown = chatItemsToMarkdown(items || []);
      await navigator.clipboard.writeText(markdown);
      toast({
        description: 'Copied template as markdown',
        autoClose: true,
        duration: 1_500
      });
    } catch (e) {
      toast({
        description: 'Failed to copy template',
        autoClose: true,
        duration: 1_500
      });
    } finally {
      setIsCopying(false);
    }
  }, [template, toast, previewRequest]);

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {template.name || <span className="text-gray-400 italic">Untitled Template</span>}
          </h3>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            disabled={isCopying}
            icon={isCopying ? <Icon name="ArrowPathIcon" className="animate-spin" /> : <Icon name="ClipboardDocumentIcon" />}
          >
            {isCopying ? 'Copying...' : 'Copy'}
          </Button>
        </div>
        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
          <span>Version {template.version || 0}</span>
        </div>
      </div>

      {template.description && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
          <div className="prose prose-sm max-w-none text-gray-600 bg-gray-50 rounded-md p-3">
            {nonHookMarkdown(template.description)}
          </div>
        </div>
      )}

      {template.templateVariables && template.templateVariables.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Template Variables</h4>
          <div className="space-y-2">
            {template.templateVariables.map((variable, i) => (
              <div key={i} className="bg-cyan-50 border border-cyan-200 rounded-md p-2 text-sm">
                <code className="font-mono text-cyan-700">{variable.key}</code>
                <span className="text-gray-500 mx-2">=</span>
                <span className="text-gray-700">{variable.value}</span>
                {variable.description && (
                  <p className="text-xs text-gray-500 mt-1">{variable.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Instructions ({template.instructions.length})
        </h4>
        {template.instructions.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No instructions defined</p>
        ) : (
          <div className="space-y-2">
            {template.instructions.map((instruction, i) => (
              <CollapsibleInstruction key={i} instruction={instruction} index={i} />
            ))}
          </div>
        )}
      </div>

      {template.output && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Output Format</h4>
          <OutputFormatPreview output={template.output} />
        </div>
      )}
    </div>
  );
}


export function MessageTemplateConfig() {
  const findTemplateRequest = usePost('/program/admin/prompts/list');
  const [loadedTemplate, setLoadedTemplate] = useState<survey.EditableMessageTemplate | null>(null);
  const location = useLocation();

  useAsyncEffect(async () => {
    const queryParams = new URLSearchParams(location.search)
    const name = queryParams.get('name');
    const version = queryParams.get('version');

    if (name) {
      const { items: templates } = await findTemplateRequest({ name, version: version ? parseInt(version) : undefined });
      const existingTemplate = templates?.[templates?.length - 1];
      if (existingTemplate) {
        const storedTemplate = { ...existingTemplate, kind: 'Message Template' } as StoredTemplate;
        setLoadedTemplate(convertStoredTemplateToEditable(storedTemplate));
        return;
      }
    }

    setLoadedTemplate(blankTemplate);
  }, []);

  if (!loadedTemplate) return <div>Loading...</div>;

  return <InnerMessageTemplateConfig initial={loadedTemplate} />;
}

export function InnerMessageTemplateConfig(props: { initial: survey.EditableMessageTemplate }) {
  const [messageTemplate, setMessageTemplate] = useState<survey.EditableMessageTemplate>(props.initial);
  const [latestVersion, setLatestVersion] = useState(props.initial.version);
  const [availableTemplates, setAvailableTemplates] = useState<TemplateOption[]>([]);
  const [showPreview, setShowPreview] = useState(!!props.initial.name);
  const [showEditor, setShowEditor] = useState(!props.initial.name);

  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const createTemplateRequest = usePost('/program/admin/prompts/create');
  const findTemplateRequest = usePost('/program/admin/prompts/list');
  const scanTemplatesRequest = usePost('/program/admin/prompts/scan');

  const { toast } = useToast();

  useAsyncEffect(async () => {
    const { items } = await scanTemplatesRequest({});

    const templatesByName = new Map();
    for (const template of items || []) {
      const existing = templatesByName.get(template.name);
      if (!existing || template.version > existing.version) {
        templatesByName.set(template.name, template);
      }
    }

    const options: TemplateOption[] = Array.from(templatesByName.values()).map(template => ({
      label: `${template.name} (v${template.version})`,
      value: `${template.name}:${template.version}`,
      template: { ...template, kind: 'Message Template' as const } as StoredTemplate
    }));

    setAvailableTemplates(options);
  }, []);

  useAsyncEffect(async () => {
    const name = messageTemplate.name?.trim();
    if (!name) {
      setLatestVersion(0);
      return;
    }

    const templates = await findTemplateRequest({ name });
    const latest = (templates.items ?? [])
      .map(item => item.version)
      .filter(v => typeof v === 'number')
      .sort((a, b) => b - a)[0] ?? 0;

    setLatestVersion(latest);
  }, [messageTemplate.name]);

  const saveTemplate = useCallback(async () => {
    const oldVersion = messageTemplate.version ?? 0;
    const newVersion = (latestVersion ?? oldVersion) + 1;
    const newTemplate = { ...messageTemplate, version: newVersion };

    if (oldVersion > 0) {
      await createTemplateRequest({ item: newTemplate });
      toast({
        description: `created new template version: v${oldVersion} => v${newVersion}`,
        autoClose: true,
        duration: 1_500
      });
    } else {
      await createTemplateRequest({ item: newTemplate });
      toast({
        description: 'created new template!',
        autoClose: true,
        duration: 1_500
      });
    }
    setMessageTemplate(newTemplate);
    setLatestVersion(newTemplate.version);
  }, [createTemplateRequest, messageTemplate, latestVersion, toast]);


  const isDerivedTemplate = useMemo(() => {
    return messageTemplate.instructions.some(instruction =>
      instruction.kind === 'Message Template Reference'
    ) || (messageTemplate.output?.type === 'template');
  }, [messageTemplate.instructions, messageTemplate.output]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  return (
    <div className="bg-gray-100 h-full p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Message Template Management</h1>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowEditor(!showEditor)}
            icon={<Icon name={showEditor ? "EyeSlashIcon" : "EyeIcon"} />}
          >
            {showEditor ? 'Hide Editor' : 'Show Editor'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            icon={<Icon name={showPreview ? "EyeSlashIcon" : "EyeIcon"} />}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </div>
      </div>

      <div className={`grid gap-4 ${showEditor && showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
        {showEditor && <div className="bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-4">
          <h2 className="text-lg font-semibold">Template Editor</h2>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <strong>Message Template References</strong>
            </div>
            <p className="mt-1 text-blue-700">
              You can reference other message templates in your instructions and output formats using "Message Template Reference" instructions.
            </p>
            <div className="mt-2 text-blue-600 text-xs">
              <p><strong>How to use:</strong></p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><strong>Instructions:</strong> Add a "Message Template Reference" instruction to include another template's content.</li>
                <li><strong>Output Format:</strong> Set output type to "template" to reference another template's output schema.</li>
              </ul>
            </div>
          </div>

          <Suspense fallback={<div>Loading editor...</div>}>
            <Distro
              ref={distroRef}
              value={messageTemplate}
              types='src/survey.ts'
              llmContext={`We are using a tool to configure message templates for AI conversations.
              Message templates define the structure and instructions for AI interactions.
              
              You can reference other message templates in two ways:
              
              1. **As Instructions**: Add a "Message Template Reference" instruction anywhere in your instructions array
                 - Select the template name and version to reference
                 - Optionally set a role (user/system/assistant) to override the referenced instructions' roles
                 - The referenced template's instructions will be inserted at that exact position
              
              2. **As Output Format**: Set the output format type to "template" 
                 - Reference another template's output format
                 - The referenced template's output schema will be used
              
              All template references are resolved when you save the template.
              This makes templates modular and reusable!
              
              Available templates: ${availableTemplates.map(t => t.template.name).join(', ')}`}
              name='EditableMessageTemplate'
              onChange={(v) => {
                setMessageTemplate(v)
              }}
            />
          </Suspense>

          <div className='flex flex-col items-center gap-8'>
            <div className='flex flex-row gap-4'>
              <Button
                className="ml-2 mb-2"
                variant="primary"
                onClick={saveTemplate}>
                {isDerivedTemplate
                  ? `Save Derived Template (v${(latestVersion ?? messageTemplate.version) + 1})`
                  : `Save New Version (v${(latestVersion ?? messageTemplate.version) + 1})`
                }
              </Button>
            </div>
          </div>
        </div>}

        {showPreview && (
          <div className="bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg p-4 overflow-auto max-h-[calc(100vh-8rem)]">
            <h2 className="text-lg font-semibold mb-4">Preview</h2>
            <MessageTemplatePreview template={messageTemplate} />
          </div>
        )}
      </div>
    </div>
  );
}
